(() => {
  const PREFIX = '[FTR Rebuy Loop Bundles]';
  const BUNDLE_PROP = '_bundleId';
  const BUNDLE_NAME_PROP = '_bundleName';
  const CUSTOM_CARD_CLASS = 'ftr-rebuy-loop-bundle';
  const HIDDEN_CLASS = 'ftr-rebuy-loop-child-hidden';
  let renderTimer = null;
  let listObserver = null;
  let observedList = null;
  let rendering = false;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function getBundleId(item) {
    return String(item?.properties?.[BUNDLE_PROP] || '').trim();
  }

  function isLoopBundleItem(item) {
    return Boolean(getBundleId(item));
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Unable to load cart (${response.status})`);
    return response.json();
  }

  function groupCartItems(cart) {
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      if (!isLoopBundleItem(item)) return;
      const id = getBundleId(item);
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    });
    return groups;
  }

  function getPlanName(items) {
    const allocation = items.find((item) => item?.selling_plan_allocation)?.selling_plan_allocation;
    return allocation?.selling_plan?.name || allocation?.name || '';
  }

  function getBundleName(items) {
    const explicit = items.find((item) => item?.properties?.[BUNDLE_NAME_PROP])?.properties?.[BUNDLE_NAME_PROP];
    return String(explicit || 'Custom Bundle').trim();
  }

  function getTotals(items) {
    return items.reduce((totals, item) => {
      totals.final += Number(item.final_line_price ?? item.line_price ?? 0);
      totals.original += Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
      totals.quantity += Number(item.quantity || 0);
      return totals;
    }, { final: 0, original: 0, quantity: 0 });
  }

  function buildChildren(items) {
    return items.map((item) => {
      const image = item.featured_image?.url || item.image || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="ftr-rebuy-loop-bundle__variant">${escapeHtml(item.variant_title)}</div>`
        : '';
      return `
        <div class="ftr-rebuy-loop-bundle__child">
          <a class="ftr-rebuy-loop-bundle__child-image" href="${escapeHtml(item.url || '#')}">
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title || '')}" loading="lazy">` : ''}
          </a>
          <div class="ftr-rebuy-loop-bundle__child-info">
            <a class="ftr-rebuy-loop-bundle__child-title" href="${escapeHtml(item.url || '#')}">${escapeHtml(item.product_title || item.title || '')}</a>
            ${variant}
            <div class="ftr-rebuy-loop-bundle__child-meta">Qty ${Number(item.quantity || 1)}</div>
          </div>
          <div class="ftr-rebuy-loop-bundle__child-price">${formatMoney(item.final_line_price ?? item.line_price ?? 0)}</div>
        </div>`;
    }).join('');
  }

  function buildBundleCard(bundleId, items) {
    const card = document.createElement('li');
    card.className = CUSTOM_CARD_CLASS;
    card.dataset.bundleId = bundleId;

    const name = getBundleName(items);
    const planName = getPlanName(items);
    const totals = getTotals(items);
    const isSubscription = Boolean(planName);
    const compare = totals.original > totals.final
      ? `<span class="ftr-rebuy-loop-bundle__compare">${formatMoney(totals.original)}</span>`
      : '';

    card.innerHTML = `
      <div class="ftr-rebuy-loop-bundle__header">
        <div>
          <div class="ftr-rebuy-loop-bundle__eyebrow">${isSubscription ? 'SUBSCRIPTION BUNDLE' : 'BUNDLE'}</div>
          <div class="ftr-rebuy-loop-bundle__title">${escapeHtml(name)}</div>
          <div class="ftr-rebuy-loop-bundle__meta">
            ${planName ? `<span>${escapeHtml(planName)}</span>` : '<span>One-time purchase</span>'}
            <span>${totals.quantity} item${totals.quantity === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div class="ftr-rebuy-loop-bundle__total">${compare}<span>${formatMoney(totals.final)}</span></div>
      </div>
      <div class="ftr-rebuy-loop-bundle__children">${buildChildren(items)}</div>
      <button type="button" class="ftr-rebuy-loop-bundle__remove" data-remove-loop-bundle="${escapeHtml(bundleId)}">Remove bundle</button>
    `;
    return card;
  }

  function restoreNativeRows(list) {
    list.querySelectorAll(`.${HIDDEN_CLASS}`).forEach((row) => row.classList.remove(HIDDEN_CLASS));
    list.querySelectorAll(`.${CUSTOM_CARD_CLASS}`).forEach((card) => card.remove());
  }

  async function renderBundles() {
    const list = document.querySelector('.rebuy-cart__flyout-items');
    if (!list || rendering) return;
    rendering = true;

    try {
      const cart = await getCart();
      const groups = groupCartItems(cart);

      if (listObserver && observedList === list) listObserver.disconnect();
      restoreNativeRows(list);

      for (const [bundleId, items] of groups.entries()) {
        const rows = Array.from(list.querySelectorAll('.rebuy-cart__flyout-item[data-bundle-id]'))
          .filter((row) => String(row.dataset.bundleId || '') === bundleId);
        if (!rows.length) continue;

        const card = buildBundleCard(bundleId, items);
        rows[0].before(card);
        rows.forEach((row) => row.classList.add(HIDDEN_CLASS));
      }
    } catch (error) {
      console.warn(PREFIX, 'Unable to group bundle lines:', error);
    } finally {
      rendering = false;
      observeList();
    }
  }

  function scheduleRender(delay = 80) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderBundles, delay);
  }

  function observeList() {
    const list = document.querySelector('.rebuy-cart__flyout-items');
    if (!list) return;
    if (observedList === list && listObserver) {
      listObserver.observe(list, { childList: true, subtree: true });
      return;
    }
    listObserver?.disconnect();
    observedList = list;
    listObserver = new MutationObserver((mutations) => {
      if (rendering) return;
      const nativeChanged = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === 1 && (node.matches?.('.rebuy-cart__flyout-item') || node.querySelector?.('.rebuy-cart__flyout-item'))
        ) || Array.from(mutation.removedNodes).some((node) =>
          node.nodeType === 1 && node.classList?.contains('rebuy-cart__flyout-item')
        );
      });
      if (nativeChanged) scheduleRender();
    });
    listObserver.observe(list, { childList: true, subtree: true });
  }

  async function removeBundle(bundleId, button) {
    if (!bundleId || button?.disabled) return;
    if (button) {
      button.disabled = true;
      button.textContent = 'Removing…';
    }
    try {
      const cart = await getCart();
      const updates = {};
      cart.items.forEach((item) => {
        if (getBundleId(item) === bundleId) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error(`Unable to remove bundle (${response.status})`);

      if (window.Rebuy?.Cart?.fetchCart) {
        await Promise.resolve(window.Rebuy.Cart.fetchCart());
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: PREFIX } }));
      scheduleRender(150);
    } catch (error) {
      console.error(PREFIX, 'Remove bundle failed:', error);
      if (button) {
        button.disabled = false;
        button.textContent = 'Remove bundle';
      }
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove-loop-bundle]');
    if (!button) return;
    event.preventDefault();
    removeBundle(button.dataset.removeLoopBundle, button);
  });

  document.addEventListener('rebuy:cart.change', () => scheduleRender(120));
  document.addEventListener('cart:updated', () => scheduleRender(120));
  document.addEventListener('cart:refresh', () => scheduleRender(120));

  const initialObserver = new MutationObserver(() => {
    if (document.querySelector('.rebuy-cart__flyout-items')) {
      observeList();
      scheduleRender(50);
      initialObserver.disconnect();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialObserver.observe(document.body, { childList: true, subtree: true });
      observeList();
      scheduleRender(50);
    }, { once: true });
  } else {
    initialObserver.observe(document.body, { childList: true, subtree: true });
    observeList();
    scheduleRender(50);
  }
})();
